import L from 'leaflet';
import { useEffect, useRef, useState, type RefObject } from 'react';
import type { LatLng } from '../../data/types';

const IMAGERY = 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}';
const ROADS = 'https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Transportation/MapServer/tile/{z}/{y}/{x}';
const PLACES = 'https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}';

// crossOrigin makes tile responses CORS-readable so the service worker can cache them for offline use
// (opaque responses would be uncacheable in practice) and the rodent snapshot can export them to PNG.
const tileOptions: L.TileLayerOptions = { minZoom: 2, maxZoom: 19, maxNativeZoom: 18, noWrap: true, keepBuffer: 4, updateWhenIdle: true, crossOrigin: 'anonymous' };

/** Esri satellite imagery with a toggleable roads and place-names overlay, as in the prototype. */
export function createSatelliteMap(container: HTMLElement, center: LatLng, zoom: number): L.Map {
  const map = L.map(container, { zoomControl: true }).setView(center, zoom);
  const imagery = L.tileLayer(IMAGERY, { ...tileOptions, attribution: 'Tiles &copy; Esri — Source: Esri, Maxar, Earthstar Geographics, and the GIS User Community' }).addTo(map);
  const details = L.layerGroup([
    L.tileLayer(ROADS, { ...tileOptions, opacity: 0.82, attribution: 'Roads &copy; Esri' }),
    L.tileLayer(PLACES, { ...tileOptions, opacity: 0.96, attribution: 'Place names &copy; Esri' })
  ]).addTo(map);
  L.control.layers({ 'Satellite imagery': imagery }, { 'Roads & place names': details }, { collapsed: true, position: 'topright' }).addTo(map);
  // Pinch-zoom and one-finger panning on field tablets and phones.
  map.dragging.enable(); map.touchZoom.enable(); map.doubleClickZoom.enable();
  container.style.touchAction = 'none';
  return map;
}

export const markerIcon = (type: string) => L.divIcon({ className: 'leaflet-service-icon', html: `<span class="leaflet-marker ${type}"></span>`, iconSize: [24, 24], iconAnchor: [12, 12] });

export function locationMarker(location: LatLng, label: string) {
  return L.circleMarker(location, { radius: 8, color: '#6d8652', weight: 3, fillColor: '#6d8652', fillOpacity: 0.28 }).bindPopup(label);
}

/**
 * Creates a Leaflet map once for the component's lifetime. Maps live in views that stay mounted while hidden,
 * so the size is recalculated whenever the view becomes visible again.
 */
export function useSatelliteMap(containerRef: RefObject<HTMLDivElement | null>, center: LatLng, zoom: number, active: boolean) {
  const [map, setMap] = useState<L.Map | null>(null);
  const created = useRef(false);
  useEffect(() => {
    if (created.current || !containerRef.current) return;
    created.current = true;
    const instance = createSatelliteMap(containerRef.current, center, zoom);
    setMap(instance);
    return () => { instance.remove(); created.current = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (!map || !active) return;
    const timer = setTimeout(() => map.invalidateSize({ pan: false, debounceMoveend: true }), 80);
    return () => clearTimeout(timer);
  }, [map, active]);
  // A tap outside an expanded layer picker collapses it (it otherwise stays open on touch screens).
  useEffect(() => {
    const collapse = (event: PointerEvent) => {
      document.querySelectorAll('.leaflet-control-layers-expanded').forEach((control) => { if (!control.contains(event.target as Node)) control.classList.remove('leaflet-control-layers-expanded'); });
    };
    document.addEventListener('pointerdown', collapse, true);
    return () => document.removeEventListener('pointerdown', collapse, true);
  }, []);
  return map;
}
